/** @format */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';

import { Observable } from 'rxjs';
import { map, retry, tap } from 'rxjs/operators';

import { DOCUMENT, KeyValue } from '@angular/common';

import { MetaService } from './meta.service';
import { HttpService, Store } from '@shared/data-access/services';
import { Page } from '../interfaces/page.interface';
import { SeoDao } from '../dao/seo.dao';
import {
	Chain,
	Environment,
	Breadcrumb,
	SEOAttributes,
	SEOTabContent,
	SanitizedSEOAttributes
} from '@shared/data-access/interfaces';
import { CustomHttpHeaders } from '@shared/data-access/enums';

@Injectable({
	providedIn: 'root'
})
export class SeoService {
	readonly DEFAULT_SDX_FILE_NAME = 'sd-default';
	private SEO_URL: string;

	constructor(
		private http: HttpClient,
		private chain: Chain,
		private environment: Environment,
		private seoDao: SeoDao,
		private store: Store,
		@Inject(DOCUMENT) private document: HTMLDocument,
		private metaService: MetaService,
		private readonly title: Title,
		private readonly httpService: HttpService
	) {
		this.SEO_URL = this.environment.api.seo;
	}

	/**
	 * @description
	 * Builds and returns a default page meta description for a given product title and chain abbreviation
	 */
	public getDefaultPageMetaDescription(
		productTitle: string,
		chainIdentifierAbbr: string
	): string {
		// Return meta description
		let metaDescription = '';

		// Switch on the chain abbreviation to return a store specific meta description
		switch (chainIdentifierAbbr) {
			case 'calia':
				// Set the meta description
				metaDescription = `Enjoy free shipping on ${productTitle} from CALIA. Read detailed descriptions and reviews on ${productTitle} and shop similar products at great prices.`;

				// Break
				break;
			case 'gg':
				// Set the meta description
				metaDescription = `Buy the ${productTitle} today and improve your game. Find product reviews and specs or compare products today at Golf Galaxy.`;

				// Break
				break;

			case 'pl':
				// Set the meta description
				metaDescription = `Shop ${productTitle} at Public Lands. Explore key product details to make sure you get the best fit for your needs.`;

				// Break
				break;
			default:
				/*
				 * DEFAULT TO DSG
				 */
				// Set the meta description
				metaDescription = `Shop a wide selection of ${productTitle} at DICK’S Sporting Goods and order online for the finest quality products from the top brands you trust.`;

				// Break
				break;
		}

		// Return the meta description
		return metaDescription;
	}

	/**
	 * @description
	 * Builds and returns a default page title for a given product title and chain name
	 */
	public getDefaultPageTitle(productTitle: string, chainName: string): string {
		// Return the default page title
		return `${productTitle} | ${chainName}`;
	}

	/**
	 * @description
	 * Trims the pipe and chain name off a page title if present
	 */
	public getTrimmedPageTitle(pageTitle: string): string {
		// Return string
		let returnString = '';

		// Check to see if the argument is a string
		if (typeof pageTitle === 'string' && pageTitle.trim() !== '') {
			// Check to see if the title has the pipe
			returnString =
				pageTitle.indexOf('|') > -1
					? pageTitle.substring(0, pageTitle.indexOf('|')).trim()
					: pageTitle;
		}

		// Return the string
		return returnString;
	}

	/**
	 * @description
	 * Gets the Page object by an ID and type.  Types: `category`, `product`, `name`.  The returned Page object will
	 * contain SEO related information.
	 */
	public getPageSeobyUrl$(url?: string, retries?: number): Observable<Page> {
		return this.seoDao.getPageSeobyUrl$(url, retries);
	}

	// ******************** FUNCTION DECLARATIONS ********************//
	/**
	 * @desc Callback function which gets called every time the SearchDex dynamic properties are updated
	 * so that a new Managed Include can be fetched
	 *
	 * @type {Function}
	 */
	public getSearchDexInclude$(inSearchDexDynamicData, retries?) {
		let filePattern;
		// Ensure we have all of our required parameters
		if (inSearchDexDynamicData) {
			filePattern = `sd-${this.chain.searchDex.pageType[inSearchDexDynamicData.pageType]}${
				inSearchDexDynamicData.pageId
			}`;
		} else {
			filePattern = 'sd-default';
		}

		// Get the Managed Include Content
		return this.seoDao.getSearchDexInclude$(filePattern, retries).pipe(
			map((response: any) => {
				if (response) {
					// Set the categoryID
					this.store.set('SEARCHDEX_RESPONSE', response);
					return response;
				}
			})
		);
	}

	getSEOData$(
		seoToken: string,
		retries = 0,
		useDefaultHttpTransferCache = false
	): Observable<any> {
		return this.seoDao.getSEOData$(seoToken, retries, useDefaultHttpTransferCache).pipe(
			map((response) => {
				response.categories.map((category) => {
					category.sanitizedAttributes = this.getSeoAttributesMapped(category.attr);
					return category;
				});
				return response;
			})
		);
	}

	private getSeoAttributesMapped(attributes: SEOAttributes): SanitizedSEOAttributes {
		return {
			seoFeaturedLinks: this.getSEOFeaturedLinksMapped(attributes),
			seoTabsContent: this.getSEOTabContentMapped(attributes),
			sdxIncludesSuppressFlag: this.getSEOAttributeValue(
				attributes,
				'SDX_INCLUDES_SUPPRESS_FLAG'
			),
			seoRobotsNoIndex: this.getSEOAttributeValue(attributes, 'SEO_ROBOTS_NOINDEX'),
			seoKeywords: this.getSEOAttributeValue(attributes, 'SEO_KEYWORDS'),
			sponsoredProducts: this.getSEOAttributeValue(attributes, 'SPONSORED_PRODUCTS'),
			layoutType: this.getSEOAttributeValue(attributes, 'LAYOUT_TYPE'),
			pageType: this.getSEOAttributeValue(attributes, 'PAGE_TYPE'),
			metaDescription: this.getSEOAttributeValue(attributes, 'META_DESCRIPTION'),
			seoRobotsNoFollow: this.getSEOAttributeValue(attributes, 'SEO_ROBOTS_NOFOLLOW'),
			breadcrumb: this.getSEOAttributeValue(attributes, 'BREADCRUMB'),
			seoNotes: this.getSEOAttributeValue(attributes, 'SEO_NOTES'),
			seoHypercatSuppress: this.getSEOAttributeValue(attributes, 'SEO_HYPERCAT_SUPPRESS'),
			defaultLinkText: this.getSEOAttributeValue(attributes, 'DEFAULT_LINK_TEXT'),
			seoHeadline: this.getSEOAttributeValue(attributes, 'SEO_HEADLINE'),
			layoutPriority: this.getSEOAttributeValue(attributes, 'LAYOUT_PRIORITY'),
			topResultThumbnail: this.getSEOAttributeValue(attributes, 'TOP_RESULT_THUMBNAIL'),
			title: this.getSEOAttributeValue(attributes, 'TITLE'),
			seoCanonical: this.getSEOAttributeValue(attributes, 'SEO_CANONICAL'),
			layoutOption: this.getSEOAttributeValue(attributes, 'LAYOUT_OPTION'),
			topResultFullImage: this.getSEOAttributeValue(attributes, 'TOP_RESULT_FULLIMAGE')
		};
	}

	private getSEOFeaturedLinksMapped(seoAttributes: SEOAttributes): KeyValue<string, string>[] {
		const featuredLinksResponse = [];
		const featuredLinks = this.getSEOAttributeValue(seoAttributes, 'SEO_FEATURED_LINKS');

		if (featuredLinks) {
			const featuredLinksParse = JSON.parse(featuredLinks);
			Object.keys(featuredLinksParse).forEach((key) => {
				featuredLinksResponse.push({ key, value: featuredLinksParse[key] });
			});
		}

		return featuredLinksResponse;
	}

	private getSEOTabContentMapped(seoAttributes: SEOAttributes): SEOTabContent[] {
		const seoTabResultList: SEOTabContent[] = [];
		const attrHeadingMatchPattern = /\bSEO_TAB\d_HEADING/;

		Object.keys(seoAttributes).forEach((key) => {
			// For each key, check if it matches the heading match pattern.
			if (key.search(attrHeadingMatchPattern) === 0 && seoAttributes[key]?.value) {
				const header = seoAttributes[key].value;
				// Swap Tab header text with tab content text, to retrieve content data.
				const contentKey = key.replace('HEADING', 'CONTENT');
				if (seoAttributes[contentKey]?.value) {
					seoTabResultList.push({
						header,
						content: seoAttributes[contentKey].value
					});
				}
			}
		});

		return seoTabResultList;
	}

	private getSEOAttributeValue(seoAttributes: SEOAttributes, key: string): string {
		return seoAttributes[key]?.value || '';
	}

	getsubCatSEOData$(seoToken: string, retries = 0): Observable<any> {
		const requestUrl = `${this.SEO_URL}seo-category/v1/categories?seoUrl=${seoToken}&storeId=${this.chain.storeId}&children=true&published=true`;
		const headersConfig: Record<string, string> = {
			'X-TIMEOUT': '10000',
			'Content-Type': 'application/json'
		};

		// Get Origin Host Header
		const origin = this.httpService.getRequestHeaderValueAndSetStateKey('ORIGIN_HOST');
		if (origin) {
			headersConfig[CustomHttpHeaders.ORIGIN] = origin;
		}

		const httpOptions = {
			headers: new HttpHeaders(headersConfig)
		};

		return this.http.get(requestUrl, httpOptions).pipe(
			map((response: any) => response),
			retry(retries)
		);
	}

	initialize(seoToken: string, pageType: string, path: string, retries = 0) {
		return this.getSEOData$(seoToken, retries).pipe(
			tap((result) => {
				this.initializePageSEO(result, path, pageType);
			})
		);
	}

	initializePageSEO(data, defaultSeoPath, pageType): void {
		const defaultCanonical = `${this.chain.metaCanonical}${defaultSeoPath}`;
		if (data.categories && data.categories[0]) {
			const seoData = data.categories[0];

			if (seoData.attr?.SEO_CANONICAL?.value) {
				this.addCanonicalTag(seoData.attr.SEO_CANONICAL.value, 'SEO Service');
			} else {
				this.addCanonicalTag(defaultCanonical);
			}

			this.createRobotsTag(
				seoData.attr.SEO_ROBOTS_NOINDEX,
				seoData.attr.SEO_ROBOTS_NOFOLLOW,
				seoData.attr.SEO_ROBOTS_FOLLOW
			);

			this.setPageMetaInformation(seoData, pageType);
		} else {
			this.addCanonicalTag(defaultCanonical);
			this.metaService.addTag(
				{
					property: 'og:url',
					content: defaultCanonical
				},
				true
			);
		}
	}

	createRobotsTag(
		noindex: { value?: string },
		nofollow: { value?: string },
		follow: { value?: string }
	) {
		const robotsParams = [];

		if (noindex?.value?.toUpperCase() === 'Y') {
			robotsParams.push('noindex');
		}

		if (nofollow?.value?.toUpperCase() === 'Y') {
			robotsParams.push('nofollow');
		}

		if (follow?.value?.toUpperCase() === 'Y') {
			robotsParams.push('follow');
		}

		if (robotsParams.length > 0) {
			const content = robotsParams.join(',');
			this.metaService.addTag({ name: 'robots', content }, true);
		}
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	setPageMetaInformation(data, pageType?) {
		const title = data.title || this.getDefaultPageTitle(data.name, this.chain.chainName);
		const description =
			data.metaDesc ||
			this.getDefaultPageMetaDescription(data.name, this.chain.chainIdentifierAbbr);
		const keywords = data.metaKeywords
			? [{ name: 'keywords', content: data.metaKeywords }]
			: [];

		this.title.setTitle(title);
		this.metaService.addTags(
			[
				{
					name: 'apple-mobile-web-app-title',
					content: title
				},
				{
					name: 'description',
					content: description
				},
				...keywords,
				{
					property: 'og:type',
					content: 'category'
				},
				{
					property: 'og:site_name',
					content: this.chain.chainName
				},
				{
					property: 'og:title',
					content:
						title.indexOf('|') > -1
							? title.substring(0, title.indexOf('|')).trim()
							: title
				},
				{
					property: 'og:description',
					content: description
				}
			],
			true
		);
	}

	setSearchAction(storeURL: string) {
		const scriptTag = document.createElement('script');

		scriptTag.type = 'application/ld+json';
		scriptTag.text = `{
			"@context": "http://schema.org",
			"@type": "WebSite",
			"url": "${storeURL}",
			"potentialAction": {
				"@type": "SearchAction",
				"target":  "${storeURL}/search/SearchDisplay?searchTerm={search_term_string}",
				"query-input": {
					"@type": "PropertyValueSpecification",
					"valueRequired": true,
					"valueMaxlength": 100,
					"valueName": "search_term_string"
				}
			}
		}`;

		document.head.appendChild(scriptTag);
	}

	addCanonicalTag(url, comment = 'Default') {
		if (this.document.head.querySelector('[rel="canonical"]')) {
			const canonicalLink: HTMLLinkElement =
				this.document.head.querySelector('[rel="canonical"]');

			canonicalLink.setAttribute('href', url);
			if (canonicalLink.dataset) {
				canonicalLink.dataset.comment = `${comment} Canonical`;
			}
		} else {
			// Create a link element
			const canonicalLink = this.document.createElement('link');

			// Set the link relationship attribute
			canonicalLink.setAttribute('rel', 'canonical');

			// Set the link href attribute
			canonicalLink.setAttribute('href', url);

			if (canonicalLink.dataset) {
				canonicalLink.dataset.comment = `${comment} Canonical`;
			}

			// Append the link element to the head element
			this.document.head.appendChild(canonicalLink);
		}
	}

	/**
	 * @description
	 * Accepts fullImage and returns updated imagePath
	 */
	getimagePath(category) {
		let imgPath = '';
		if (category.thumbnail) {
			if (category.thumbnail.includes('Attachment/')) {
				imgPath = `${this.chain.metaCanonical}/wcsstore/${this.chain.chainIdentifier}/${category.thumbnail}`;
			} else {
				// eslint-disable-next-line no-useless-escape
				const thumbnailName = category.thumbnail.replace(/[\/ -]/g, '_');
				imgPath = `${this.environment.thirdParty.scene7.url}${thumbnailName}/?${this.environment.thirdParty.scene7.suffix}`;
			}
		} else if (category.attr && category.attr['TOP_RESULT_THUMBNAIL']) {
			// eslint-disable-next-line no-useless-escape
			const ecode = category.attr['TOP_RESULT_THUMBNAIL'].value.replace(/[\/ -]/g, '_');
			imgPath = `${this.environment.thirdParty.scene7.url}${ecode}/?${this.environment.thirdParty.scene7.suffix}`;
		} else {
			imgPath = '';
		}
		return imgPath;
	}

	/**
	 * @description
	 * Gets breadcrumbs for a given category ID
	 */
	getBreadCrumbsData$(identifier: string, retries = 1): Observable<Breadcrumb[]> {
		return this.seoDao.getBreadCrumbsData$(identifier, retries);
	}
}
